import { useEffect, useState } from 'react';

import { conversationTopic, type Message } from '../../api/communication';
import { realtime } from '../../api/runs';

/**
 * A thread, live (`06 §7`, criterion 1 of the phase).
 *
 * The same shape as `useRunStream` and for the same reason: **the stream is an
 * optimisation, not the source of truth.** The messages are fetched from
 * `GET /conversations/{id}/messages` first, the subscription only saves the
 * browser from polling, and every frame is merged **by id** — a message
 * delivered twice by the hub must not draw twice, and the initial fetch and the
 * first frames overlap by construction.
 *
 * What arrives on the wire is deliberately thinner than what the endpoint
 * returns: a live frame has no `decision` attached, because composing one costs
 * a query on the write path. A blocked attempt therefore appears immediately
 * with `blocked: true` and gains its policy link on the next refetch — the
 * message is what matters in the second it happens, the policy link is what
 * matters when somebody clicks it.
 */

interface Frame {
  type: 'message';
  message_id: string;
  conversation_id: string;
  from: string;
  from_name: string;
  message_type: Message['type'];
  priority: Message['priority'];
  status: string;
  subject: string | null;
  body: string;
  blocked: boolean;
  created_at: string;
}

const RECONNECT_MS = 3_000;

export function useConversationStream(
  companyId: string,
  conversationId: string | null,
  onFrame: (frame: Frame) => void,
): { connected: boolean } {
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (companyId === '' || conversationId === null) {
      setConnected(false);

      return;
    }

    let source: EventSource | null = null;
    let reconnect: ReturnType<typeof setTimeout> | null = null;
    let stopped = false;

    const open = async (): Promise<void> => {
      try {
        const grant = await realtime.grant();

        if (stopped) return;

        const url = new URL(grant.hub_url, window.location.origin);
        url.searchParams.append('topic', conversationTopic(companyId, conversationId));

        source = new EventSource(url.toString(), { withCredentials: true });
        source.onopen = () => setConnected(true);

        source.onmessage = (event: MessageEvent<string>) => {
          try {
            const payload = JSON.parse(event.data) as Frame;

            if (payload.type === 'message') onFrame(payload);
          } catch {
            // A frame this client cannot read is not a reason to tear the
            // stream down: the database still has everything.
          }
        };

        source.onerror = () => {
          setConnected(false);
          source?.close();
          source = null;

          if (!stopped) reconnect = setTimeout(() => void open(), RECONNECT_MS);
        };
      } catch {
        setConnected(false);
      }
    };

    void open();

    return () => {
      stopped = true;
      if (reconnect !== null) clearTimeout(reconnect);
      source?.close();
      setConnected(false);
    };
  }, [companyId, conversationId, onFrame]);

  return { connected };
}
